// src/components/HomePage.js
import React, { useEffect, useState, useRef, useContext } from 'react';
import { Link } from 'react-router-dom';
import './HomePage.css';
import { LanguageContext } from '../translations/LanguageContext';
import Footer from '../components/Footer'; // Importing the Footer component

function HomePage() {
  const { language, translations } = useContext(LanguageContext);
  const content = translations[language].homePage;

  const firstLineText = content.firstLine;
  const secondLineText = content.secondLine;

  const [displayedFirstLine, setDisplayedFirstLine] = useState('');
  const [displayedSecondLine, setDisplayedSecondLine] = useState('');
  const [isTypingSecondLine, setIsTypingSecondLine] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);

  const typingSpeed = 75;
  const pauseBetweenLines = 250;
  const totalAnimationDuration = 26000;

  const typingTimeoutRef = useRef(null);
  const restartTimeoutRef = useRef(null);
  const firstLineIndexRef = useRef(0);
  const secondLineIndexRef = useRef(0);
  const imageRef = useRef(null); // Ref for the profile image

  // Reset typing animation when language changes
  useEffect(() => {
    setDisplayedFirstLine('');
    setDisplayedSecondLine('');
    firstLineIndexRef.current = 0;
    secondLineIndexRef.current = 0;
    setIsTypingSecondLine(false);
  }, [language]);

  useEffect(() => {
    let isCancelled = false;

    const typeFirstLine = () => {
      if (isCancelled) return;

      if (firstLineIndexRef.current < firstLineText.length) {
        const nextChar = firstLineText[firstLineIndexRef.current];
        setDisplayedFirstLine((prev) => prev + nextChar);
        firstLineIndexRef.current++;
        typingTimeoutRef.current = setTimeout(typeFirstLine, typingSpeed);
      } else {
        typingTimeoutRef.current = setTimeout(typeSecondLine, pauseBetweenLines);
      }
    };

    const typeSecondLine = () => {
      if (isCancelled) return;

      setIsTypingSecondLine(true);

      if (secondLineIndexRef.current < secondLineText.length) {
        const nextChar = secondLineText[secondLineIndexRef.current];
        setDisplayedSecondLine((prev) => prev + nextChar);
        secondLineIndexRef.current++;
        typingTimeoutRef.current = setTimeout(typeSecondLine, typingSpeed);
      } else {
        setIsTypingSecondLine(false);

        const typingDuration =
          firstLineText.length * typingSpeed +
          pauseBetweenLines +
          secondLineText.length * typingSpeed;
        const remainingTime = totalAnimationDuration - typingDuration;

        if (remainingTime > 0) {
          restartTimeoutRef.current = setTimeout(restartTyping, remainingTime);
        } else {
          restartTyping();
        }
      }
    };

    const restartTyping = () => {
      if (isCancelled) return;
      setDisplayedFirstLine('');
      setDisplayedSecondLine('');
      firstLineIndexRef.current = 0;
      secondLineIndexRef.current = 0;
      setIsTypingSecondLine(false);
      typingTimeoutRef.current = setTimeout(typeFirstLine, typingSpeed);
    };

    // Start typing
    typingTimeoutRef.current = setTimeout(typeFirstLine, typingSpeed);

    return () => {
      isCancelled = true;
      clearTimeout(typingTimeoutRef.current);
      clearTimeout(restartTimeoutRef.current);
    };
  }, [firstLineText, secondLineText, typingSpeed, pauseBetweenLines, totalAnimationDuration, language]);

  useEffect(() => {
    const handleScroll = () => {
      const atBottom = window.innerHeight + window.scrollY >= document.body.scrollHeight - 1;
      setIsAtBottom(atBottom);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleArrowClick = () => {
    if (isAtBottom) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    }
  };

  // Function to insert line breaks based on newline characters
  const formatDescription = (text) => {
    const lines = text.split('\n');
    return lines.map((line, index) => (
      <React.Fragment key={index}>
        {line}
        {index < lines.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  const handleMouseMove = (e) => {
    const { left, top, width, height } = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - left) / width - 0.5;
    const y = (e.clientY - top) / height - 0.5;
    
    // Tilt intensity
    const tilt = 20; 

    // Calculate rotation (push effect: mouse side goes down/away)
    const rotateY = x * tilt; 
    const rotateX = -y * tilt;

    if (imageRef.current) {
      imageRef.current.style.transition = 'transform 0.1s ease-out';
      imageRef.current.style.transform = `perspective(1000px) rotateY(${rotateY}deg) rotateX(${rotateX}deg) scale3d(1.02, 1.02, 1.02)`;
    }
  };

  const handleMouseLeave = () => {
    if (imageRef.current) {
      imageRef.current.style.transition = 'transform 0.5s ease-out';
      imageRef.current.style.transform = 'perspective(1000px) rotateY(0deg) rotateX(0deg) scale3d(1, 1, 1)';
    }
  };

  return (
    <div className="home-page">
      <div className="hero">
        <div className="hero-content">
          <div className="typing-container">
            <h1 className="typewriter first-line" aria-label={firstLineText}>
              {displayedFirstLine}
              {displayedFirstLine.length < firstLineText.length && <span className="cursor"></span>}
            </h1>
            <h2 className="typewriter second-line" aria-label={secondLineText}>
              {displayedSecondLine}
              {(isTypingSecondLine || displayedSecondLine.length > 0) && <span className="cursor"></span>}
            </h2>
          </div>
          <p className="hero-description">{formatDescription(content.description)}</p>
          <p className="hero-location">{content.location}</p>
        </div>
        <div 
          className="hero-image-wrapper"
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          <img
            ref={imageRef}
            src="/assets/HomePageMe.png"
            alt="Gianni Carozza"
            className="hero-image"
          />
        </div>
        <div
          className={`arrow-container ${isAtBottom ? 'up' : 'down'}`}
          onClick={handleArrowClick}
          role="button"
          tabIndex={0}
          onKeyPress={(e) => {
            if (e.key === 'Enter') handleArrowClick();
          }}
          aria-label={isAtBottom ? 'Scroll to top' : 'Scroll to bottom'}
        >
          <span className={`arrow ${isAtBottom ? 'up' : 'down'}`}></span>
        </div>
      </div>

      <div className="project-section">
        <Link to="/uni-projects" className="project-card">
          <img
            src="/assets/UniversityProjectsCover.png"
            alt={content.universityProjects}
            className="project-image"
          />
          <div className="project-text">
            <h3>{content.universityProjects}</h3>
            <p>{content.universitySubtitle}</p>
          </div>
        </Link>
        <Link to="/personal-projects" className="project-card">
          <img
            src="/assets/PersonalProjectsCover.png"
            alt={content.personalProjects}
            className="project-image"
          />
          <div className="project-text">
            <h3>{content.personalProjects}</h3>
            <p>{content.personalSubtitle}</p>
          </div>
        </Link>
      </div>

      <div className="cta-section">
        <h2>{content.cta}</h2>
        <Link to="/contact" className="contact-button">
          {content.contact}
        </Link>
      </div>

      {/* Replaced the footer with the Footer component */}
      <Footer />
    </div>
  );
}

export default HomePage;
